import React, { useCallback, useRef, useState } from 'react'
import { Theme } from '@mui/material'
import { SelectChangeEvent } from '@mui/material'
import {
  ITableComponentInstance,
  ITableColumnConfig,
  ITableStyles,
  TableSortDirection,
  DEFAULT_TABLE_STYLES,
} from 'src/types/report-designer'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { generateUUID } from 'src/utils/function'

export const createNewColumn = (existingColumnsLength: number): ITableColumnConfig => ({
  id: generateUUID(),
  formSchemaFieldId: null,
  fieldName: '',
  orderNumber: existingColumnsLength + 1,
  width: null,
  schemaFieldType: null,
})

export const applyDraftColumn = (
  instance: ITableComponentInstance,
  draftColumn: ITableColumnConfig,
): ITableComponentInstance => {
  const updatedColumns = instance.columns.map(col => (col.id === draftColumn.id ? draftColumn : col))
  return { ...instance, columns: updatedColumns }
}

export const applyFieldToDraft = (draftColumn: ITableColumnConfig, field: IFormSchemaColumn): ITableColumnConfig => ({
  ...draftColumn,
  formSchemaFieldId: field.identifier,
  fieldName: field.name,
  schemaFieldType: field.type,
})

export const parseDraftWidth = (value: string): number | null => {
  if (value === '') return null
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? null : Math.max(1, parsed)
}

export const updateTableStyle = <K extends keyof ITableStyles>(
  instance: ITableComponentInstance,
  key: K,
  value: ITableStyles[K],
): ITableComponentInstance => ({
  ...instance,
  tableStyles: { ...instance.tableStyles, [key]: value },
})

export const applySortColumn = (instance: ITableComponentInstance, value: string): ITableComponentInstance => ({
  ...instance,
  sortColumnId: value === '' ? null : value,
  sortDirection: value === '' ? TableSortDirection.None : instance.sortDirection ?? TableSortDirection.Asc,
})

export const applySortDirection = (
  instance: ITableComponentInstance,
  value: TableSortDirection,
): ITableComponentInstance => ({
  ...instance,
  sortDirection: value,
})

export const removeColumn = (instance: ITableComponentInstance, columnId: string): ITableColumnConfig[] =>
  instance.columns.filter(col => col.id !== columnId).map((col, idx) => ({ ...col, orderNumber: idx + 1 }))

export const reorderColumns = (
  columns: ITableColumnConfig[],
  fromIndex: number,
  toIndex: number,
): ITableColumnConfig[] => {
  const reordered = [...columns]
  const [moved] = reordered.splice(fromIndex, 1)
  reordered.splice(toIndex, 0, moved)
  return reordered.map((col, idx) => ({ ...col, orderNumber: idx + 1 }))
}

export const parseRowHeight = (value: string, theme: Theme): number => {
  const defaultHeight = DEFAULT_TABLE_STYLES(theme).rowHeight
  if (value === '') return defaultHeight
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? defaultHeight : Math.max(1, parsed)
}

export interface IUseTableHandlersParams {
  instance: ITableComponentInstance
  onChange: (updated: ITableComponentInstance) => void
  columns?: IFormSchemaColumn[]
  theme: Theme
}

export const useTableComponentHandlers = ({ instance, onChange, columns, theme }: IUseTableHandlersParams) => {
  const dragColumnIndex = useRef<number | null>(null)
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(null)
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null)
  const [draftColumn, setDraftColumn] = useState<ITableColumnConfig | null>(null)

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...instance, label: e.target.value })
    },
    [instance, onChange],
  )

  const handleAddColumn = useCallback(() => {
    const newColumn: ITableColumnConfig = createNewColumn(instance.columns.length)
    setEditingColumnId(newColumn.id)
    setDraftColumn({ ...newColumn })
  }, [instance.columns.length])

  const handleEditColumn = useCallback((column: ITableColumnConfig) => {
    setEditingColumnId(column.id)
    setDraftColumn({ ...column })
  }, [])

  const handleSaveColumn = useCallback(() => {
    if (!draftColumn) return
    const existsInList = instance.columns.some(col => col.id === draftColumn.id)
    if (existsInList) {
      onChange(applyDraftColumn(instance, draftColumn))
    } else {
      const newColumns = [...instance.columns, draftColumn].map((col, idx) => ({ ...col, orderNumber: idx + 1 }))
      onChange({ ...instance, columns: newColumns })
    }
    setEditingColumnId(null)
    setDraftColumn(null)
  }, [draftColumn, instance, onChange])

  const handleDraftFieldSelect = useCallback(
    (fieldId: string) => {
      const field = columns?.find(f => f.identifier === fieldId)
      if (!field || !draftColumn) return
      setDraftColumn(applyFieldToDraft(draftColumn, field))
    },
    [draftColumn, columns],
  )

  const handleDraftWidthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!draftColumn) return
      setDraftColumn({ ...draftColumn, width: parseDraftWidth(e.target.value) })
    },
    [draftColumn],
  )

  const handleTableStyleChange = useCallback(
    <K extends keyof ITableStyles>(key: K, value: ITableStyles[K]) => {
      onChange(updateTableStyle(instance, key, value))
    },
    [instance, onChange],
  )

  const handleSortColumnChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      onChange(applySortColumn(instance, e.target.value))
    },
    [instance, onChange],
  )

  const handleSortDirectionChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      onChange(applySortDirection(instance, e.target.value as TableSortDirection))
    },
    [instance, onChange],
  )

  const handleDeleteColumn = useCallback(
    (columnId: string) => {
      const isSortColumn = instance.sortColumnId === columnId
      onChange({
        ...instance,
        columns: removeColumn(instance, columnId),
        ...(isSortColumn && { sortColumnId: null, sortDirection: TableSortDirection.None }),
      })
      if (editingColumnId === columnId) {
        setEditingColumnId(null)
        setDraftColumn(null)
      }
    },
    [instance, onChange, editingColumnId],
  )

  const handleColumnDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragColumnIndex.current = index
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleColumnDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragColumnIndex.current === null || dragColumnIndex.current === index) return
    setDragOverColumnIndex(index)
  }, [])

  const handleColumnDrop = useCallback(
    (e: React.DragEvent, toIndex: number) => {
      e.preventDefault()
      e.stopPropagation()
      const fromIndex = dragColumnIndex.current
      if (fromIndex === null || fromIndex === toIndex) {
        dragColumnIndex.current = null
        setDragOverColumnIndex(null)
        return
      }
      onChange({ ...instance, columns: reorderColumns(instance.columns, fromIndex, toIndex) })
      dragColumnIndex.current = null
      setDragOverColumnIndex(null)
    },
    [instance, onChange],
  )

  const handleColumnDragEnd = useCallback(() => {
    dragColumnIndex.current = null
    setDragOverColumnIndex(null)
  }, [])

  const handleCancelEdit = useCallback(() => {
    setEditingColumnId(null)
    setDraftColumn(null)
  }, [])

  const handleRowHeightChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleTableStyleChange('rowHeight', parseRowHeight(e.target.value, theme))
    },
    [handleTableStyleChange, theme],
  )

  return {
    dragOverColumnIndex,
    editingColumnId,
    draftColumn,
    handleLabelChange,
    handleAddColumn,
    handleEditColumn,
    handleSaveColumn,
    handleDraftFieldSelect,
    handleDraftWidthChange,
    handleTableStyleChange,
    handleSortColumnChange,
    handleSortDirectionChange,
    handleDeleteColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    handleColumnDragEnd,
    handleCancelEdit,
    handleRowHeightChange,
  }
}
